"use client";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import { Stack, Typography } from "@mui/material";
import { CheckRounded as FreeTierIcon } from "@mui/icons-material";
// import ParamsIcon from "@mui-symbols-material/w400/NetworkIntelligenceOutlined";
import DiamondIcon from "@mui-symbols-material/w400/DiamondRounded";
import { CurrencyBitcoinRounded as PricingIcon } from "@mui-symbols-material/w400";

import { observer, useStore } from "../../../service/mobx";
import { ModelAvatar } from "../../../component/Card/Model";
import ModelTaskIcon from "../../../component/Icon/ModelTask";
import Skeleton from "../../../component/Skeleton";
import ToolTip from "../../../component/Tooltip";
import CopyButton from "../../../component/CopyButton";

const ClusterStatus = dynamic(() => import("./ClusterStatus"));

function Header({ size = "large" }) {
  const { model, device } = useStore();
  const chips = [
    {
      label: model.node.closedSource ? "closed" : "open",
      borderColor: "var(--outline-variant)",
      tooltip: `This is a ${
        model.node.closedSource ? "closed" : "open"
      } source model`
    },
    {
      label: model.node.task,
      borderColor: "var(--outline-variant)",
      color: "var(--secondary-color)",
      Icon: props => <ModelTaskIcon task={model.node.task} {...props} />
    },
    // !model.node.closedSource && model.node.params
    //   ? {
    //       label: `${new Intl.NumberFormat("en-US", {
    //         notation: "compact"
    //       })
    //         .format(model.node.params)
    //         .toLowerCase()} params`,
    //       bgcolor: "var(--secondary-container)",
    //       color: "var(--secondary-on-container)",
    //       Icon: ParamsIcon
    //     }
    //   : undefined,
    (model.tier.free && !model.premiumPlan) ||
    model.tier.premium ||
    model.tier.enterprise
      ? {
          label: model.tier.free
            ? "Free tier"
            : model.tier.premium
              ? "pay-as-you-go"
              : "Enterprise",
          bgcolor: model.tier.free
            ? "#DDF8D8"
            : model.tier.premium
              ? "var(--colors-warning-95)"
              : "var(--secondary-container)",
          color: model.tier.free
            ? "var(--colors-success-30)"
            : model.tier.premium
              ? "var(--colors-warning-30)"
              : "var(--secondary-on-container)",
          Icon: model.tier.free
            ? FreeTierIcon
            : model.tier.premium
              ? DiamondIcon
              : undefined,
          tooltip: model.allowed
            ? model.tier.free
              ? "Free, limited access. For unlimited access, upgrade to Pay-As-You-Go"
              : "You've got unlimited access to this model"
            : undefined
        }
      : undefined,
    model.node.closedSource
      ? undefined
      : model.getPrice()
        ? {
            Icon: PricingIcon,
            label: `$${model.getPrice()} / sec`,
            borderColor: "var(--outline-variant)",
            tooltip:
              "Open models run on serverless GPUs. They cost fractions of credits per second to run"
          }
        : undefined,
    // NOTE this should eventually replace model.getPrice(), we will write all cost information to nodes directly
    // removing any need for the client to know anything about them
    ...JSON.parse(model.node.costTagsJSON ?? "[]").map(({ name }) => ({
      Icon: PricingIcon,
      label: name,
      borderColor: "var(--outline-variant)",
      tooltip:
        "Open models run on serverless GPUs. They cost fractions of credits per token run"
    }))
  ].filter(defined => defined);

  return (
    <Stack spacing={3} width="100%">
      <Stack spacing={2} direction="row" alignItems="center">
        <ModelAvatar
          model={model.node}
          avatar={`/api/model/avatar?org=${model.node.org}${
            model.node.repo?.hfOwner?.avatar
              ? `&imgUrl=${btoa(model.node.repo.hfOwner?.avatar)}`
              : ""
          }`}
        />
        <Typography
          color="var(--secondary-color)"
          typography={
            size === "large"
              ? {
                  compact: "headlineSm",
                  expanded: "headlineMd",
                  large: "headlineLg",
                  extraLarge: "displaySm"
                }
              : {
                  compact: "titleMd",
                  extraLarge: "titleLg"
                }
          }
          sx={{ textWrap: "balance" }}
        >
          {model.node.modelId ?? model.node.name}
        </Typography>
        {!device.isPhone && (
          <CopyButton
            size="small"
            sx={{
              borderRadius: 1,
              color: "var(--secondary-color)",
              height: { compact: 56, expanded: 28 },
              fontSize: 8,
              textWrap: "balance",
              textAlign: "center",
              textOverflow: "ellipsis",
              opacity: 0.5,
              "&p": { compact: "labelSm", expanded: "labelMd" },
              "&:hover": {
                opacity: 1
              }
            }}
          />
        )}
      </Stack>
      <Stack spacing={1} direction="row" alignItems="center" flexWrap="wrap">
        <Suspense
          fallback={<Skeleton width={72} height="100%" borderRadius="sm" />}
        >
          <ClusterStatus
            status={model.node.closedSource ? "ready" : undefined}
          />
        </Suspense>
        {chips.map(
          ({ label, bgcolor, borderColor, color, Icon, tooltip = "" }) => (
            <ToolTip
              // Unique key error fix
              // Without String() this can be NaN for some reason, this is a bandaid
              // TODO figure out why this is happening
              key={String(label)}
              title={tooltip}
            >
              <Stack
                flex={{ compact: 1, expanded: "inherit" }}
                alignItems="center"
                height={{ compact: 56, expanded: 28 }}
                border={borderColor ? 1 : undefined}
                borderColor={borderColor}
                p={{ compact: 0.75, expanded: 0.5 }}
                pb={{ compact: 0, expanded: 0.5 }}
                bgcolor={bgcolor}
                spacing={{ compact: 0, expanded: 0.5 }}
                borderRadius="var(--shape-xs-round)"
                pr={{ compact: 0.75, expanded: 1.5 }}
                pl={Icon ? undefined : { compact: 0.75, expanded: 1.5 }}
                justifyContent="center"
                direction={{ compact: "column", expanded: "row" }}
              >
                {Icon ? <Icon sx={{ color, width: 16, height: 16 }} /> : null}
                <Typography
                  color={color}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  sx={{ textWrap: "balance", textAlign: "center" }}
                  typography={{ compact: "labelSm", expanded: "labelMd" }}
                >
                  {label}
                </Typography>
              </Stack>
            </ToolTip>
          )
        )}
      </Stack>
    </Stack>
  );
}

export default observer(Header);
